'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSearchBox } from 'react-instantsearch';
import { ALGOLIA_SEARCH_QUERY_DEBOUNCE_MS } from 'commons/constants';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import IconSearch from 'components/icons/IconSearch';
import { colorGrayLight2 } from 'styles/layout/colors';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import useDebouncedAlgoliaQueryHook from 'components/consumer/consumer-search/consumer-search-modal-next/components/JBSearchBoxNext/useDebouncedAlgoliaQueryHook';
const CategorySearchBox = ({
  pageName = ''
}) => {
  const {
    refine,
    query
  } = useSearchBox();
  const {
    onSearchQueryChange,
    searchQuery
  } = useAdvancedSearchContext();
  const [inputValue, setInputValue] = useState(query ?? '');
  const latestQueryRef = useRef(query ?? '');
  const inputRef = useRef(null);
  const refineRef = useRef(refine);
  const onSearchQueryChangeRef = useRef(onSearchQueryChange);
  // Same timer as header search. Skip the modal reset registry so closing
  // the header search does not cancel this field.
  const {
    queryHook
  } = useDebouncedAlgoliaQueryHook(ALGOLIA_SEARCH_QUERY_DEBOUNCE_MS, {
    registerReset: false
  });
  refineRef.current = refine;
  onSearchQueryChangeRef.current = onSearchQueryChange;
  useEffect(() => {
    const nextQuery = query ?? '';
    const inputFocused = inputRef.current && document.activeElement === inputRef.current;

    // refine lands after the pause. While the field is focused, the typed
    // value is ahead of InstantSearch — don't pull the input back.
    if (inputFocused && latestQueryRef.current !== nextQuery) return;
    latestQueryRef.current = nextQuery;
    setInputValue(nextQuery);
  }, [query]);
  useEffect(() => {
    const nextSearchQuery = searchQuery ?? '';
    if (nextSearchQuery === latestQueryRef.current) return;
    latestQueryRef.current = nextSearchQuery;
    setInputValue(nextSearchQuery);
    refineRef.current(nextSearchQuery);
  }, [searchQuery]);
  const handleChange = useCallback(event => {
    const value = event?.target?.value ?? '';
    setInputValue(value);
    latestQueryRef.current = value;
    queryHook(value, nextQuery_0 => {
      refineRef.current(nextQuery_0);
      onSearchQueryChangeRef.current?.(nextQuery_0);
    });
  }, [queryHook]);
  return <LabeledTextInput ref={inputRef} action={<IconSearch color={colorGrayLight2} height={20} />} actionAlignment="left" label={`Search ${pageName}`} labelSize="sm" noMargin onChange={handleChange} value={inputValue ?? null} />;
};
CategorySearchBox.propTypes = {
  pageName: PropTypes.string
};
export default CategorySearchBox;